import type React from 'react';

import { useMutation } from '@tanstack/react-query';
import { CrossCircleIcon } from '@vlabs/icons';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { toast } from 'react-toastify';

import { getUser, patchUser } from '@vlabs/api/artemy/client';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useDetachDepartmentUsersTranslation } from './i18n';
import type { DetachDepartmentUsersProps } from './types';

type DetachDepartmentUsersIconButtonProps = DetachDepartmentUsersProps & Omit<IconButtonProps, 'onClick'>;

const displayName = 'DetachDepartmentUsersIconButton';
const testid = buildTestid(displayName);

function DetachDepartmentUsersIconButton({
  departmentId,
  userIds,
  'data-testid': dataTestId = undefined,
  ...props
}: DetachDepartmentUsersIconButtonProps): React.JSX.Element {
  const { t } = useDetachDepartmentUsersTranslation();
  const detachDepartmentUsers = useMutation({
    mutationFn: async () => {
      await Promise.all(userIds.map(async (userId) => {
        const user = await getUser(userId);

        await patchUser(userId, {
          department_ids: (user.department_ids ?? []).filter((currentDepartmentId) => (
            currentDepartmentId !== departmentId
          )),
        });
      }));
    },
    onError: () => {
      toast.error(t('toast_error'));
    },
  });

  return (
    <IconButton
      disabled={props.disabled ?? detachDepartmentUsers.isPending}
      icon={<CrossCircleIcon />}
      kind="negative"
      onClick={() => detachDepartmentUsers.mutate()}
      title={t('title')}
      variant="dimmed"
      {...testid(dataTestId)}
      {...props}
    />
  );
}

DetachDepartmentUsersIconButton.displayName = displayName;

export { DetachDepartmentUsersIconButton };
